import {
  AngledHighwayElement,
  CircleElement,
  Color,
  GroupElement,
  GroupLogicalElement,
  HighwayElement,
  HorizontalAlignment,
  ImageCenteredElement,
  Interactivity,
  LineElement,
  LinePathElement,
  NgonElement,
  NoneElement,
  RackElement,
  RectCenteredElement,
  Stripe,
  TextElement,
  VerticalAlignment,
} from '@thive/canvas';
import { Rectangle } from '@thive/fddm-types';
import { MathUtils, Vector2, Vector3 } from 'three';

import { DEFAULT_RACK } from '@/modules/common/constants/storage';
import { NumUtils } from '@/modules/common/utils/NumUtils';
import { VectorUtils } from '@/modules/common/utils/VectorUtils';
import {
  RACKING_BEAM_COLOR,
  RACKING_POST_COLOR,
} from '@/modules/canvas/mappers/visualization3DConstants';
import { ArrowImageType } from '../elements/imageElements';

export const METERS_TO_MILLIMETERS_FACTOR = 1000;

interface BaseElementParams {
  id: string;
  tags: string[];
  parentId?: string;
  visible?: boolean;
}

interface Stroke {
  color: string;
  width: number;
}

interface CreateRectangleParams extends BaseElementParams {
  rectangle: Rectangle;
  z: number;
  stroke?: Stroke;
  stripe?: Stripe;
  color?: string;
  textureVariant?: ArrowImageType | 'rack';
  interactivity?: {
    draggable?: boolean;
    rotatable?: boolean;
    scalable?: boolean;
    selectable?: boolean;
    snappable?: boolean;
    clickable?: boolean;
    hoverable?: boolean;
  };
}

interface CreateLineParams extends BaseElementParams {
  id: string;
  name: string;
  pointA: Vector3;
  pointB: Vector3;
  stroke?: Stroke;
  tags: string[];
  interactivity: {
    draggable?: boolean;
    rotatable?: boolean;
    scalable?: boolean;
    selectable?: boolean;
    snappable?: boolean;
    clickable?: boolean;
  };
}

interface CreateGroupParams extends BaseElementParams {
  id: string;
  name: string;
  interactivity: {
    draggable?: boolean;
    rotatable?: boolean;
    scalable?: boolean;
    selectable?: boolean;
    snappable?: boolean;
    clickable?: boolean;
  };
  tags: string[];
  position?: Vector3;
  rotation?: Vector3;
}

interface CreateGroupLogicalParams extends BaseElementParams {
  id: string;
  name: string;
  interactivity: {
    draggable?: boolean;
    rotatable?: boolean;
    scalable?: boolean;
    selectable?: boolean;
    snappable?: boolean;
    clickable?: boolean;
    editable?: boolean;
  };
  tags: string[];
}

interface CreateSegmentElementParams extends BaseElementParams {
  color: string;
  points: Vector3[];
  z: number;
  stroke?: Stroke;
  interactivity?: {
    draggable?: boolean;
    rotatable?: boolean;
    selectable?: boolean;
    editable?: boolean;
  };
}

interface CreateAngledHighwayElementParams extends BaseElementParams {
  color: string;
  points: Vector3[];
  z: number;
  vehicleWidth: number;
  laneGap: number;
  margin: number;
  laneColor: string;
  stroke?: Stroke;
  interactivity?: {
    draggable?: boolean;
    rotatable?: boolean;
    scalable?: boolean;
    selectable?: boolean;
    editable?: boolean;
    snappable?: boolean;
  };
}

interface CreateHighwayElementParams extends BaseElementParams {
  color: string;
  rectangle: Rectangle;
  z: number;
  vehicleWidth: number;
  laneGap: number;
  margin: number;
  laneColor: string;
  interactivity?: {
    draggable?: boolean;
    rotatable?: boolean;
    scalable?: boolean;
    selectable?: boolean;
    snappable?: boolean;
    clickable?: boolean;
  };
}

interface CreateImageElementParams extends BaseElementParams {
  name: string;
  position: Vector3;
  width: number;
  height: number;
  url: string;
  rotation: Vector3;
  interactivity?: {
    draggable?: boolean;
    rotatable?: boolean;
    selectable?: boolean;
  };
}

interface CreateTextElementParams extends BaseElementParams {
  name: string;
  text: string;
  position: Vector3;
  fontSize: number;
  color: string;
  backgroundColor?: string;
  backgroundPadding?: number;
  horizontalAlignment: HorizontalAlignment;
  verticalAlignment: VerticalAlignment;
  alwaysFaceCamera?: boolean;
  rotation?: Vector3;
  wrapping?: boolean;
  autoScale?: boolean;
  width?: number;
  height?: number;
  interactivity?: {
    draggable?: boolean;
    rotatable?: boolean;
    scalable?: boolean;
    selectable?: boolean;
    snappable?: boolean;
    clickable?: boolean;
  };
}

interface CreateCircleElementParams extends BaseElementParams {
  radius: number;
  color: string;
  position: { x: number; y: number; z: number };
  stroke?: Stroke;
  interactivity?: {
    draggable: boolean;
    rotatable: boolean;
    selectable: boolean;
    clickable: boolean;
  };
}

interface CreateNoneElementParams extends BaseElementParams {
  name?: string;
  position: Vector3;
  rotation?: Vector3;
  interactivity?: { draggable: boolean; rotatable: boolean; selectable: boolean };
}

interface CreateNgonParams extends BaseElementParams {
  color: string;
  position: Vector3;
  rotation: Vector3;
  points: Vector2[];
  interactivity: Interactivity;
}

interface CreateRackParams extends BaseElementParams {
  name: string;
  position: Vector3;
  rotation: Vector3;
  frameLength: number;
  frameDepth: number;
  shelfHeights: number[];
  shelfLength: number;
  bumpers?: boolean;
  horizontalBeams?: boolean;
  postSize?: number;
}

interface IFactory {
  buildRectangleElement(params: CreateRectangleParams): RectCenteredElement;
  buildLineElement(params: CreateLineParams): LineElement;
  buildSegmentElement(params: CreateSegmentElementParams): LinePathElement;
  buildImageElement(params: CreateImageElementParams): ImageCenteredElement;
  buildTextElement(params: CreateTextElementParams): TextElement;
  buildCircleElement(params: CreateCircleElementParams): CircleElement;
  buildNoneElement(params: CreateNoneElementParams): NoneElement;
  buildNgonElement(params: CreateNgonParams): NgonElement;
  buildRackElement(params: CreateRackParams): RackElement;
}

class Factory implements IFactory {
  buildRectangleElement(params: CreateRectangleParams): RectCenteredElement {
    const {
      id,
      color,
      rectangle,
      interactivity,
      parentId,
      z,
      stroke,
      stripe,
      tags,
      visible,
      textureVariant,
    } = params;

    const fallbackFill = color ? Color.fromHex(color) : Color.TRANSPARENT;
    const fill = textureVariant ? Color.fromHex('#FFFFFF') : fallbackFill;

    return {
      id,
      name: id,
      parentId,
      position: new Vector3(
        NumUtils.metersToMmRounded(rectangle.centerX),
        NumUtils.metersToMmRounded(rectangle.centerY),
        z,
      ),
      type: 'RectCentered',
      fill,
      size: new Vector2(
        NumUtils.metersToMmRounded(rectangle.length),
        NumUtils.metersToMmRounded(rectangle.width),
      ),
      stroke: stroke && Color.fromHex(stroke.color),
      strokeWidth: stroke && stroke.width,
      stripe,
      rotation: new Vector3(0, 0, MathUtils.radToDeg(rectangle.angle)),
      interactivity,
      tags,
      visible,
      materialModifier: textureVariant ? { textureVariant } : undefined,
    };
  }

  buildLineElement(params: CreateLineParams): LineElement {
    const { id, name, parentId, pointA, pointB, stroke, interactivity, tags } = params;

    return {
      id,
      name,
      type: 'Line',
      parentId,
      pointA,
      pointB,
      strokeWidth: stroke && stroke.width,
      autoStrokeWidth: true,
      stroke: stroke && Color.fromHex(stroke.color),
      interactivity,
      tags,
    };
  }

  buildGroupElement(params: CreateGroupParams): GroupElement {
    const { id, name, parentId, interactivity, position, rotation, tags } = params;

    return {
      id,
      name,
      type: 'Group',
      parentId,
      position,
      rotation,
      interactivity,
      tags,
    };
  }

  buildGroupLogicalElement(params: CreateGroupLogicalParams): GroupLogicalElement {
    const { id, name, parentId, interactivity, tags } = params;

    return {
      id,
      name,
      type: 'GroupLogical',
      parentId,
      interactivity,
      tags,
    };
  }

  buildTextElement(params: CreateTextElementParams): TextElement {
    const {
      id,
      name,
      parentId,
      position,
      fontSize,
      color,
      backgroundColor,
      backgroundPadding = 0,
      verticalAlignment,
      horizontalAlignment,
      text,
      tags,
      visible,
      width,
      height,
      interactivity,
      alwaysFaceCamera = false,
      rotation = new Vector3(0, 0, 0),
      wrapping = false,
      autoScale = false,
    } = params;

    return {
      id,
      name,
      parentId,
      position: new Vector3(
        NumUtils.metersToMmRounded(position.x),
        NumUtils.metersToMmRounded(position.y),
        position.z,
      ),
      type: 'Text',
      text,
      fontSize,
      color: Color.fromHex(color),
      backgroundColor: backgroundColor ? Color.fromHex(backgroundColor) : Color.TRANSPARENT,
      backgroundPadding,
      horizontalAlignment,
      verticalAlignment,
      rotation,
      tags,
      visible,
      alwaysFaceCamera,
      interactivity,
      wraping: wrapping,
      width,
      height,
      autoScale,
    };
  }

  buildSegmentElement(params: CreateSegmentElementParams): LinePathElement {
    const { id, interactivity, parentId, z, stroke, tags, points, visible } = params;

    return {
      id,
      name: id,
      parentId,
      // NOTE: always setting to 0,0,0. DT treats the linePath points as absolute, not relative to an additional position
      position: new Vector3(0, 0, 0),
      points: points.map((v) => VectorUtils.metersToMm(v).setZ(z)),
      type: 'LinePath',
      stroke: stroke && Color.fromHex(stroke.color),
      strokeWidth: stroke && NumUtils.metersToMmRounded(stroke.width),
      interactivity,
      tags,
      visible,
    };
  }

  buildAngledHighwayElement(params: CreateAngledHighwayElementParams): AngledHighwayElement {
    const {
      id,
      interactivity,
      parentId,
      z,
      stroke,
      tags,
      points,
      vehicleWidth,
      laneGap,
      margin,
      laneColor,
      visible,
    } = params;

    return {
      id,
      name: id,
      parentId,
      // NOTE: always setting to 0,0,0. DT treats the linePath points as absolute, not relative to an additional position
      position: new Vector3(0, 0, 0),
      points: points.map((v) => VectorUtils.metersToMm(v).setZ(z)),
      type: 'AngledHighway',
      vehicleWidth: NumUtils.metersToMmRounded(vehicleWidth),
      laneGap: NumUtils.metersToMmRounded(laneGap),
      margin: NumUtils.metersToMmRounded(margin),
      laneColor: Color.fromHex(laneColor),
      stroke: stroke && Color.fromHex(stroke.color),
      strokeWidth: stroke && NumUtils.metersToMmRounded(stroke.width),
      interactivity,
      tags,
      visible,
    };
  }

  buildHighwayElement(params: CreateHighwayElementParams): HighwayElement {
    const {
      id,
      color,
      rectangle,
      interactivity,
      parentId,
      z,
      tags,
      vehicleWidth,
      laneGap,
      margin,
      laneColor,
      visible,
    } = params;

    return {
      id,
      name: id,
      parentId,
      position: new Vector3(
        NumUtils.metersToMmRounded(rectangle.centerX),
        NumUtils.metersToMmRounded(rectangle.centerY),
        z,
      ),
      type: 'Highway',
      vehicleWidth: NumUtils.metersToMmRounded(vehicleWidth),
      laneGap: NumUtils.metersToMmRounded(laneGap),
      margin: NumUtils.metersToMmRounded(margin),
      laneColor: Color.fromHex(laneColor),
      fill: Color.fromHex(color),
      // Preserve the rectangle's semantic axes. Do not sort or normalize these dimensions.
      width: NumUtils.metersToMmRounded(rectangle.length),
      height: NumUtils.metersToMmRounded(rectangle.width),
      rotation: new Vector3(0, 0, MathUtils.radToDeg(rectangle.angle)),
      interactivity,
      tags,
      visible,
    };
  }

  buildCircleElement = (params: CreateCircleElementParams): CircleElement => {
    const { id, parentId, position, color, stroke, radius, tags, visible, interactivity } = params;

    const positionInMm = new Vector3(
      NumUtils.metersToMmRounded(position.x),
      NumUtils.metersToMmRounded(position.y),
      position.z,
    );

    return {
      id,
      name: id,
      parentId,
      type: 'Circle',
      // The canvas Circle overwrites its position with its center, so the center must carry the position.
      center: positionInMm.clone(),
      radius: NumUtils.metersToMmRounded(radius),
      fill: Color.fromHex(color),
      stroke: stroke && Color.fromHex(stroke.color),
      strokeWidth: stroke && stroke.width,
      interactivity: interactivity ?? { draggable: false, rotatable: false, selectable: false },
      position: positionInMm,
      tags,
      visible,
    };
  };

  buildImageElement(params: CreateImageElementParams): ImageCenteredElement {
    const {
      id,
      name,
      parentId,
      width,
      height,
      url,
      interactivity,
      tags,
      rotation,
      position,
      visible,
    } = params;

    return {
      id,
      name,
      parentId,
      rotation,
      position: new Vector3(
        NumUtils.metersToMmRounded(position.x),
        NumUtils.metersToMmRounded(position.y),
        position.z,
      ),
      type: 'ImageCentered',
      width,
      height,
      url,
      interactivity,
      tags,
      visible,
    };
  }

  /** @description This element is purely for grouping other elements together */
  buildNoneElement(params: CreateNoneElementParams): NoneElement {
    const {
      id,
      name,
      parentId,
      position,
      rotation = new Vector3(0, 0, 0),
      interactivity,
      tags,
    } = params;

    return {
      id,
      name: name ?? id,
      parentId,
      position: new Vector3(
        NumUtils.metersToMmRounded(position.x),
        NumUtils.metersToMmRounded(position.y),
        position.z,
      ),
      rotation,
      type: 'None',
      interactivity,
      tags,
    };
  }

  buildNgonElement(params: CreateNgonParams): NgonElement {
    const { id, color, position, rotation, points, parentId, tags, visible, interactivity } =
      params;

    return {
      type: 'Ngon',
      id,
      name: id,
      parentId,
      position: new Vector3(
        NumUtils.metersToMmRounded(position.x),
        NumUtils.metersToMmRounded(position.y),
        position.z,
      ),
      rotation,
      points,
      fill: Color.fromHex(color),
      interactivity,
      tags,
      visible,
    };
  }

  buildRackElement(params: CreateRackParams): RackElement {
    const {
      id,
      name,
      parentId,
      position,
      rotation,
      tags,
      frameLength,
      frameDepth,
      shelfHeights,
      shelfLength,
      bumpers,
      horizontalBeams,
      postSize,
    } = params;

    return {
      id,
      name,
      type: 'Rack',
      position: VectorUtils.metersToMm(position),
      rotation,
      tags,
      frameLength: NumUtils.metersToMmRounded(frameLength),
      frameDepth: NumUtils.metersToMmRounded(frameDepth),
      shelfHeights: shelfHeights.map((h) => NumUtils.metersToMmRounded(h)),
      shelfLength: NumUtils.metersToMmRounded(shelfLength),
      beamSize: DEFAULT_RACK.BEAM_HEIGHT,
      postSize:
        postSize !== undefined ? NumUtils.metersToMmRounded(postSize) : DEFAULT_RACK.POST_WIDTH,
      beamColor: RACKING_BEAM_COLOR,
      postColor: RACKING_POST_COLOR,
      leftBumper: bumpers,
      rightBumper: bumpers,
      parentId,
      horizontalBeams,
    };
  }
}

export const factory = new Factory();

// Only export these private functions in tests
// eslint-disable-next-line no-underscore-dangle
export const __testOnly__ = {
  /* add private functions like so; */
  // someFunc: Factory.prototype['someFunc'],
  AreaFactory: Factory,
};
